import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import PageHeader from '../../components/layout/PageHeader';
import Table from '../../components/ui/Table';
import Spinner from '../../components/ui/Spinner';
import EmptyState from '../../components/ui/EmptyState';
import RequestActions, { CandidateCell, ReviewedCell, fmtDate, selectColumn } from '../../components/requests/RequestActions';
import { listStepSubmissionRequests, approveStepSubmissionRequest, rejectStepSubmissionRequest } from '../../api/candidates';
import Button from '../../components/ui/Button';
import ApproveAssignModal from '../../components/requests/ApproveAssignModal';
import { ASSIGNED_JOBS_QUERY_KEY } from '../../hooks/useAssignedJobsCount';
import RequestFilters, { StatusBadge, useRequestFilters } from '../../components/requests/RequestFilters';
import { BulkActionBar, useRowSelection } from '../../components/requests/BulkSelection';

import { STEP_SUBMISSION_REQUESTS_QUERY_KEY, STEP_SUBMISSION_REQUESTS_COUNT_QUERY_KEY } from '../../hooks/useStepSubmissionCount';

const ACTION_LABEL = { approve: 'Approve', reject: 'Reject', pass: 'Pass', fail: 'Fail', skip: 'Skip', complete: 'Complete', attempt: 'Fail attempt' };

const columnsFor = (selection, refetch) => [
  selectColumn(selection, (r) => r.id, (r) => r.status === 'pending'),
  { key: 'candidate', label: 'Candidate', render: (row) => <CandidateCell candidate={row.candidate} /> },
  {
    key: 'step',
    label: 'Step',
    render: (row) => (
      <div>
        {row.step.title}
        {row.step.remarks && <div className="text-xs text-gray-400">{row.step.remarks}</div>}
      </div>
    ),
  },
  { key: 'proposed_action', label: 'Action', render: (row) => ACTION_LABEL[row.proposed_action] || row.proposed_action },
  {
    key: 'requested',
    label: 'Requested',
    render: (row) => (
      <div>
        {row.requested_by}
        <div className="text-xs text-gray-400">{fmtDate(row.requested_at)}</div>
        {row.request_note && <div className="text-xs italic text-gray-500">"{row.request_note}"</div>}
      </div>
    ),
  },
  {
    key: 'status',
    label: 'Status',
    render: (row) => (
      <div>
        <StatusBadge status={row.status} />
        <ReviewedCell row={row} />
      </div>
    ),
  },
  {
    key: 'actions',
    label: '',
    render: (row) => (
      <RequestActions
        pending={row.status === 'pending'}
        onApprove={() => approveStepSubmissionRequest(row.id)}
        onReject={(reason) => rejectStepSubmissionRequest(row.id, reason)}
        onResolved={refetch}
        approvedMessage="Request approved — the action has been applied."
      >
        {row.status === 'pending' && <ApproveAssignButton approvalId={row.id} onDone={refetch} />}
      </RequestActions>
    ),
  },
];

function ApproveAssignButton({ approvalId, onDone }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button variant="secondary" onClick={() => setOpen(true)}>
        Approve &amp; assign
      </Button>
      {open && <ApproveAssignModal approvalId={approvalId} open={open} onClose={() => setOpen(false)} onDone={onDone} />}
    </>
  );
}

const searchFields = (row) => [
  row.candidate.full_name,
  row.candidate.reference_no,
  row.candidate.contact_no,
  row.candidate.nationality,
  row.candidate.category,
  row.step.title,
  row.requested_by,
];
const requestedAt = (row) => row.requested_at;

export default function StepSubmissionRequests() {
  const queryClient = useQueryClient();
  const { filters, set, apply, reset, dirty, candidate } = useRequestFilters(searchFields, requestedAt);
  const query = useQuery({
    queryKey: [...STEP_SUBMISSION_REQUESTS_QUERY_KEY, filters.status, candidate.params],
    queryFn: () => listStepSubmissionRequests({ ...candidate.params, status: filters.status }),
  });
  const allRows = query.data || [];
  const rows = apply(allRows);
  const selection = useRowSelection(rows, (r) => r.id, (r) => r.status === 'pending');

  const refetch = () => {
    queryClient.invalidateQueries({ queryKey: STEP_SUBMISSION_REQUESTS_QUERY_KEY });
    queryClient.invalidateQueries({ queryKey: STEP_SUBMISSION_REQUESTS_COUNT_QUERY_KEY });
    queryClient.invalidateQueries({ queryKey: ASSIGNED_JOBS_QUERY_KEY });
    queryClient.invalidateQueries({ queryKey: ['scheduled-steps'] });
  };

  return (
    <div>
      <PageHeader
        title="Step Approval"
        description="Pass / fail / skip actions submitted on workflows that require approval — nothing takes effect until approved here."
      />

      <RequestFilters candidate={candidate} filters={filters} set={set} reset={reset} dirty={dirty} total={allRows.length} shown={rows.length} />

      <BulkActionBar
        selection={selection}
        onApprove={approveStepSubmissionRequest}
        onReject={rejectStepSubmissionRequest}
        onDone={refetch}
      />

      {query.isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner />
        </div>
      ) : rows.length === 0 ? (
        <EmptyState title={dirty ? 'No matching requests' : 'Nothing pending'} message={dirty ? 'No requests match the current filters.' : 'No step submission requests are currently awaiting review.'} />
      ) : (
        <Table columns={columnsFor(selection, refetch)} data={rows} />
      )}
    </div>
  );
}
